import React, { useState, useEffect } from 'react';
import { Plus, TrendingDown, Edit, Trash2, Users, Building, Wrench, CheckCircle, AlertTriangle, Clock } from 'lucide-react';
import { Card, Button, Input, Select, Table, Modal, Badge, ConfirmDialog } from '../components/ui';
import { egresosService, proveedoresService, personalService } from '../services/api';
import { formatMoney, formatDate, getLocalDate } from '../utils/formatters';
import { CATEGORIAS_EGRESO, SUBCATEGORIAS_EGRESO, MESES } from '../utils/constants';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const METODOS_PAGO = [
  { value: 'EFECTIVO', label: 'Efectivo' },
  { value: 'TRANSFERENCIA', label: 'Transferencia Bancaria' },
  { value: 'YAPE', label: 'Yape' },
  { value: 'PLIN', label: 'Plin' },
  { value: 'CHEQUE', label: 'Cheque' },
  { value: 'TARJETA', label: 'Tarjeta' }
];

const EgresosPage = () => {
  const { usuario } = useAuth();
  const esPropietario = usuario?.rol === 'PROPIETARIO';
  const [egresos, setEgresos] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [personal, setPersonal] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalPagar, setModalPagar] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [filters, setFilters] = useState({ mes: new Date().getMonth() + 1, anio: new Date().getFullYear() });

  const [form, setForm] = useState({
    fecha: getLocalDate(),
    monto: '',
    categoria: 'GASTO_FIJO',
    subcategoria: '',
    observaciones: '',
    proveedor_id: '',
    personal_id: '',
    numero_factura: '',
    fecha_vencimiento: '',
    estado: 'PENDIENTE',
    metodo_pago: '',
    fecha_pago: '',
    referencia_pago: ''
  });

  const [formPagar, setFormPagar] = useState({
    fecha_pago: getLocalDate(),
    metodo_pago: '',
    referencia: ''
  });

  useEffect(() => {
    loadData();
  }, [filters]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [egresosRes, provRes, personalRes] = await Promise.all([
        egresosService.getAll(filters),
        proveedoresService.getAll(),
        personalService.getAll()
      ]);
      setEgresos(egresosRes.data.data || []);
      setProveedores(provRes.data.data || []);
      setPersonal(personalRes.data.data || []);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selected) {
        await egresosService.update(selected.id, form);
        toast.success('Egreso actualizado');
      } else {
        await egresosService.create(form);
        toast.success('Egreso registrado');
      }
      setModalOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error al guardar');
    }
  };

  const handleEdit = (egreso) => {
    setSelected(egreso);
    setForm({
      fecha: egreso.fecha?.split('T')[0] || egreso.fecha,
      monto: egreso.monto,
      categoria: egreso.categoria,
      subcategoria: egreso.subcategoria || '',
      observaciones: egreso.observaciones || '',
      proveedor_id: egreso.proveedor_id || '',
      personal_id: egreso.personal_id || '',
      numero_factura: egreso.numero_factura || '',
      fecha_vencimiento: egreso.fecha_vencimiento || '',
      estado: egreso.estado || 'PENDIENTE',
      metodo_pago: egreso.metodo_pago || '',
      fecha_pago: egreso.fecha_pago?.split('T')[0] || egreso.fecha_pago || '',
      referencia_pago: egreso.referencia_pago || ''
    });
    setModalOpen(true);
  };

  const handleDelete = async () => {
    try {
      await egresosService.delete(selected.id);
      toast.success('Egreso eliminado');
      setConfirmOpen(false);
      loadData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleMarcarPagado = async () => {
    if (!formPagar.metodo_pago) {
      toast.error('El método de pago es requerido');
      return;
    }

    try {
      await egresosService.marcarPagado(selected.id, formPagar);
      toast.success('Egreso marcado como pagado');
      setModalPagar(false);
      setFormPagar({
        fecha_pago: getLocalDate(),
        metodo_pago: '',
        referencia: ''
      });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error al marcar como pagado');
    }
  };

  const resetForm = () => {
    setSelected(null);
    setForm({
      fecha: getLocalDate(),
      monto: '',
      categoria: 'GASTO_FIJO',
      subcategoria: '',
      concepto: '',
      proveedor_id: '',
      personal_id: '',
      numero_factura: '',
      observaciones: '',
      fecha_vencimiento: '',
      metodo_pago: '',
      referencia_pago: ''
    });
  };

  const totalEgresos = egresos.reduce((sum, e) => sum + parseFloat(e.monto || 0), 0);

  // Totales por categoría
  const totalPersonal = egresos.filter(e => e.categoria === 'PERSONAL').reduce((s, e) => s + parseFloat(e.monto || 0), 0);
  const totalGastosFijos = egresos.filter(e => e.categoria === 'GASTO_FIJO').reduce((s, e) => s + parseFloat(e.monto || 0), 0);
  const totalGastosVariables = egresos.filter(e => e.categoria === 'GASTO_VARIABLE').reduce((s, e) => s + parseFloat(e.monto || 0), 0);

  const categoryColors = {
    PERSONAL: 'info',
    GASTO_FIJO: 'warning',
    GASTO_VARIABLE: 'purple'
  };

  const categoryIcons = {
    PERSONAL: Users,
    GASTO_FIJO: Building,
    GASTO_VARIABLE: Wrench
  };

  const columns = [
    { header: 'Fecha', render: (row) => formatDate(row.fecha) },
    {
      header: 'Vencimiento',
      render: (row) => {
        if (!row.fecha_vencimiento) return '-';
        const venc = new Date(row.fecha_vencimiento);
        const hoy = new Date();
        const diffDays = Math.ceil((venc - hoy) / (1000 * 60 * 60 * 24));

        if (row.estado === 'VENCIDO' || (row.estado === 'PENDIENTE' && diffDays < 0)) {
          return <span className="text-rose-400 flex items-center gap-1">
            <AlertTriangle size={14} />
            {formatDate(row.fecha_vencimiento)}
          </span>;
        } else if (diffDays <= 7) {
          return <span className="text-amber-400 flex items-center gap-1">
            <Clock size={14} />
            {formatDate(row.fecha_vencimiento)}
          </span>;
        }
        return <span className="text-slate-400">{formatDate(row.fecha_vencimiento)}</span>;
      }
    },
    { header: 'Concepto', accessor: 'concepto' },
    {
      header: 'Estado',
      render: (row) => {
        const variants = {
          PENDIENTE: 'warning',
          PAGADO: 'success',
          VENCIDO: 'danger'
        };
        return <Badge variant={variants[row.estado] || 'default'}>{row.estado}</Badge>;
      }
    },
    {
      header: 'Categoría', render: (row) => (
        <Badge variant={categoryColors[row.categoria] || 'default'}>
          {CATEGORIAS_EGRESO.find(c => c.value === row.categoria)?.label || row.categoria}
        </Badge>
      )
    },
    { header: 'Proveedor', render: (row) => row.proveedor_nombre || '-' },
    {
      header: 'Método',
      render: (row) => row.metodo_pago || '-'
    },
    { header: 'Monto', render: (row) => <span className="font-semibold text-rose-400">{formatMoney(row.monto)}</span> },
    {
      header: 'Acciones', render: (row) => (
        <div className="flex gap-2">
          {(row.estado === 'PENDIENTE' || row.estado === 'VENCIDO') && (
            <button
              onClick={() => { setSelected(row); setModalPagar(true); }}
              className="p-1.5 rounded-lg hover:bg-slate-700 text-emerald-400 hover:text-emerald-300"
              title="Marcar como pagado"
            >
              <CheckCircle size={16} />
            </button>
          )}
          <button onClick={() => handleEdit(row)} className="p-1.5 rounded-lg hover:bg-slate-700 text-slate-400 hover:text-white">
            <Edit size={16} />
          </button>
          <button onClick={() => { setSelected(row); setConfirmOpen(true); }} className="p-1.5 rounded-lg hover:bg-slate-700 text-slate-400 hover:text-rose-400">
            <Trash2 size={16} />
          </button>
        </div>
      )
    }
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Egresos</h1>
          <p className="text-slate-400">Gestión de gastos del edificio</p>
        </div>
        {!esPropietario && (
          <Button icon={Plus} onClick={() => { resetForm(); setModalOpen(true); }}>
            Nuevo Egreso
          </Button>
        )}
      </div>

      {/* Stats por categoría */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-rose-500/20">
              <TrendingDown className="text-rose-400" size={20} />
            </div>
            <div>
              <p className="text-xs text-slate-400">Total del Mes</p>
              <p className="text-xl font-bold text-rose-400">{formatMoney(totalEgresos)}</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-500/20">
              <Users className="text-blue-400" size={20} />
            </div>
            <div>
              <p className="text-xs text-slate-400">Personal</p>
              <p className="text-xl font-bold text-blue-400">{formatMoney(totalPersonal)}</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-500/20">
              <Building className="text-amber-400" size={20} />
            </div>
            <div>
              <p className="text-xs text-slate-400">Gastos Fijos</p>
              <p className="text-xl font-bold text-amber-400">{formatMoney(totalGastosFijos)}</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-purple-500/20">
              <Wrench className="text-purple-400" size={20} />
            </div>
            <div>
              <p className="text-xs text-slate-400">Gastos Variables</p>
              <p className="text-xl font-bold text-purple-400">{formatMoney(totalGastosVariables)}</p>
            </div>
          </div>
        </Card>
      </div>

      <Card className="p-4">
        <div className="flex flex-wrap gap-4">
          <Select label="Mes" value={filters.mes} onChange={(e) => setFilters({ ...filters, mes: e.target.value })} options={MESES} className="w-40" />
          <Select label="Año" value={filters.anio} onChange={(e) => setFilters({ ...filters, anio: e.target.value })} options={[{ value: 2024, label: '2024' }, { value: 2025, label: '2025' }]} className="w-32" />
          <Select label="Categoría" value={filters.categoria || ''} onChange={(e) => setFilters({ ...filters, categoria: e.target.value })} options={CATEGORIAS_EGRESO} placeholder="Todas" className="w-48" />
        </div>
      </Card>

      <Card className="overflow-hidden">
        <Table columns={columns} data={egresos} loading={loading} />
      </Card>

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title={selected ? 'Editar Egreso' : 'Nuevo Egreso'} size="lg">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input label="Fecha" type="date" value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })} required />
            <Input label="Monto (S/)" type="number" step="0.01" value={form.monto} onChange={(e) => setForm({ ...form, monto: e.target.value })} required />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Categoría"
              value={form.categoria}
              onChange={(e) => setForm({ ...form, categoria: e.target.value, subcategoria: '' })}
              options={CATEGORIAS_EGRESO}
              required
            />
            <Select
              label="Subcategoría"
              value={form.subcategoria}
              onChange={(e) => setForm({ ...form, subcategoria: e.target.value })}
              options={SUBCATEGORIAS_EGRESO[form.categoria] || []}
              placeholder="Seleccionar (opcional)"
            />
          </div>

          {/* Estado */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Estado *</label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="estado"
                  value="PENDIENTE"
                  checked={form.estado === 'PENDIENTE'}
                  onChange={(e) => setForm({ ...form, estado: e.target.value })}
                  className="text-blue-500"
                />
                <span className="text-slate-300">Pendiente</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="estado"
                  value="PAGADO"
                  checked={form.estado === 'PAGADO'}
                  onChange={(e) => setForm({ ...form, estado: e.target.value })}
                  className="text-blue-500"
                />
                <span className="text-slate-300">Pagado</span>
              </label>
            </div>
          </div>

          <Input
            label="Observaciones"
            value={form.observaciones}
            onChange={(e) => setForm({ ...form, observaciones: e.target.value })}
            placeholder="Descripción del gasto (opcional)"
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {form.categoria === 'PERSONAL' ? (
              <Select
                label="Personal"
                value={form.personal_id}
                onChange={(e) => setForm({ ...form, personal_id: e.target.value })}
                options={personal.map(p => ({ value: p.id, label: p.nombre }))}
                placeholder="Seleccionar personal"
              />
            ) : (
              <Select
                label="Proveedor"
                value={form.proveedor_id}
                onChange={(e) => setForm({ ...form, proveedor_id: e.target.value })}
                options={proveedores.map(p => ({ value: p.id, label: p.nombre }))}
                placeholder="Seleccionar (opcional)"
              />
            )}
            <Input label="N° Factura/Comprobante" value={form.numero_factura} onChange={(e) => setForm({ ...form, numero_factura: e.target.value })} />
          </div>

          {/* Fecha de Vencimiento - solo para PENDIENTE */}
          {form.estado === 'PENDIENTE' && (
            <Input
              label="Fecha de Vencimiento (opcional)"
              type="date"
              value={form.fecha_vencimiento}
              onChange={(e) => setForm({ ...form, fecha_vencimiento: e.target.value })}
            />
          )}

          {/* Campos de pago (solo si estado es PAGADO) */}
          {form.estado === 'PAGADO' && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Fecha de Pago *"
                  type="date"
                  value={form.fecha_pago}
                  onChange={(e) => setForm({ ...form, fecha_pago: e.target.value })}
                  required
                />
                <Select
                  label="Método de Pago *"
                  value={form.metodo_pago}
                  onChange={(e) => setForm({ ...form, metodo_pago: e.target.value })}
                  options={METODOS_PAGO}
                  required
                />
              </div>
              <Input
                label="Referencia / N° Operación *"
                value={form.referencia_pago}
                onChange={(e) => setForm({ ...form, referencia_pago: e.target.value })}
                placeholder="Ej: OP-123456"
                required
              />
              <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                <p className="text-sm text-emerald-300">
                  Este egreso se registrará como PAGADO y se agregará inmediatamente al flujo de caja.
                </p>
              </div>
            </>
          )}

          {form.fecha_vencimiento && (
            <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
              <p className="text-sm text-amber-300">
                Este egreso quedará PENDIENTE. Podrás marcarlo como pagado más tarde.
              </p>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="secondary" onClick={() => setModalOpen(false)}>Cancelar</Button>
            <Button type="submit">{selected ? 'Actualizar' : 'Guardar'}</Button>
          </div>
        </form>
      </Modal>

      {/* Modal Marcar como Pagado */}
      <Modal
        isOpen={modalPagar}
        onClose={() => setModalPagar(false)}
        title="Marcar Egreso como Pagado"
      >
        {selected && (
          <div className="space-y-4">
            <div className="p-4 rounded-xl bg-slate-700/50">
              <p className="text-sm text-slate-400">Egreso:</p>
              <p className="font-medium text-white">{selected.concepto}</p>
              <p className="text-2xl font-bold text-rose-400 mt-2">{formatMoney(selected.monto)}</p>
            </div>

            <Input
              label="Fecha de Pago *"
              type="date"
              value={formPagar.fecha_pago}
              onChange={(e) => setFormPagar({ ...formPagar, fecha_pago: e.target.value })}
              required
            />

            <Select
              label="Método de Pago *"
              value={formPagar.metodo_pago}
              onChange={(e) => setFormPagar({ ...formPagar, metodo_pago: e.target.value })}
              options={METODOS_PAGO}
              placeholder="Seleccionar..."
              required
            />

            <Input
              label="Referencia / N° Operación"
              value={formPagar.referencia}
              onChange={(e) => setFormPagar({ ...formPagar, referencia: e.target.value })}
              placeholder="Ej: OP-123456"
            />

            <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
              <p className="text-sm text-emerald-300">
                El egreso se marcará como PAGADO y se registrará en el flujo de caja.
              </p>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="secondary" onClick={() => setModalPagar(false)}>
                Cancelar
              </Button>
              <Button onClick={handleMarcarPagado} icon={CheckCircle}>
                Marcar como Pagado
              </Button>
            </div>
          </div>
        )}
      </Modal>

      <ConfirmDialog isOpen={confirmOpen} onClose={() => setConfirmOpen(false)} onConfirm={handleDelete} title="Eliminar Egreso" message="¿Estás seguro de eliminar este egreso?" />
    </div>
  );
};

export default EgresosPage;
